/**
 * Splitting a file between buyers.
 *
 * The daily job this replaces is opening a file, selecting ten thousand rows,
 * pasting them into a new sheet and saving it — once per buyer. Here it is a
 * block size and a download each.
 *
 * Each block is an ordinary export bounded by row number, so it carries the
 * changes the file currently has and nothing else needs to know a split
 * happened. Nothing is written: the file is untouched.
 */
import { useState } from 'react';
import {
  Alert, Button, Group, Modal, NumberInput, Stack, Table, Text,
} from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { downloadSheet } from '../api/upload';
import type { SheetFilter } from '../api/queries';
import { formatNumber } from '../theme';

export function SheetSplitModal({
  sheetId,
  fileName,
  totalRows,
  filters,
  opened,
  onClose,
}: {
  sheetId: string | null;
  fileName: string;
  /** Rows to split, after the view's filters — not the size of the file. */
  totalRows: number;
  /** Carried into every block, so the pieces are pieces of what was shown. */
  filters: SheetFilter[];
  opened: boolean;
  onClose: () => void;
}) {
  const [size, setSize] = useState(10000);
  const [busy, setBusy] = useState<number | null>(null);

  const blocks = size > 0 ? Math.ceil(totalRows / size) : 0;
  // A file split into hundreds of pieces is a mistake, not a plan — and each
  // piece is a download the browser has to be told to accept.
  const tooMany = blocks > 50;

  async function download(index: number) {
    const from = index * size + 1;
    const to = Math.min(totalRows, from + size - 1);
    setBusy(index);
    try {
      await downloadSheet(sheetId!, fileName, undefined, { from, to }, filters);
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Download failed',
        message: (e as Error).message,
      });
    }
    setBusy(null);
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Split this file" size="md">
      <Stack>
        <NumberInput
          label="Rows per file"
          description="The last one takes whatever is left over."
          min={1}
          max={Math.max(1, totalRows)}
          step={1000}
          value={size}
          onChange={(v) => setSize(Math.max(1, Number(v) || 1))}
        />

        <Text size="sm">
          {formatNumber(totalRows)} rows makes{' '}
          <Text span fw={700}>{formatNumber(blocks)}</Text> file{blocks === 1 ? '' : 's'}.
        </Text>

        {tooMany ? (
          <Alert color="chartAmber" variant="light">
            <Text size="sm">
              That is {formatNumber(blocks)} separate downloads. Use a larger block size. A
              browser will not thank you for more than about fifty.
            </Text>
          </Alert>
        ) : (
          <Table>
            <Table.Tbody>
              {Array.from({ length: blocks }, (_, i) => {
                const from = i * size + 1;
                const to = Math.min(totalRows, from + size - 1);
                return (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Text size="sm">
                        Rows {formatNumber(from)}–{formatNumber(to)}
                      </Text>
                    </Table.Td>
                    <Table.Td w={130}>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        loading={busy === i}
                        leftSection={<IconDownload size={12} />}
                        onClick={() => download(i)}
                      >
                        Download
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}

        <Text size="xs" c="dimmed">
          Each file carries the changes this one currently has. Nothing is written, so the
          file itself is untouched.
          {filters.length > 0
            ? ' These blocks are of the rows your filters match, not the whole file.'
            : ''}
        </Text>

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
